import { PayloadAction, ReducerCreators } from '@reduxjs/toolkit';
import { serializeError } from '@/store/utils/serialize-error';

import { SpeakersState } from './types';
import { RootState } from '@/store/main';
import { FetchSpeakers, UpdateSpeaker } from '@/connection/speakers/speakers';
import { Speaker } from '@/types/speakers.types';
import toastTrigger from '@ui/toast/toast-trigger';

export const reducers = (create: ReducerCreators<SpeakersState>) => ({
  fetchSpeakers: create.asyncThunk(
    async (
      { channel, returnStoreStateIfExists = true }: { channel: number; returnStoreStateIfExists?: boolean },
      { getState }
    ) => {
      if (returnStoreStateIfExists) {
        // return from state if exists
        const state = getState() as RootState;
        const speakers = state?.speakers?.speakers as Speaker[] | null;
        if (speakers) {
          return speakers;
        }
      }
      return FetchSpeakers(channel);
    },
    {
      fulfilled: (state, action) => {
        state.speakers = action.payload;
        state.loadingSpeakers = false;
      },
      pending: (state) => {
        state.loadingSpeakers = true;
      },
      rejected: (state, action) => {
        state.loadingSpeakers = false;
        console.error(action.error);
        // TODO
        // add an error to state and show alert in the UI component
      },
    }
  ),
  updateSpeaker: create.asyncThunk(
    async (
      {
        speaker,
        baseLanguage,
        languages,
      }: {
        /** originalSpeaker used to reset speakers on the project sessions should the api call fail */
        originalSpeaker: Speaker;
        speaker: Speaker;
        baseLanguage: string;
        languages: string[];
      },
      { rejectWithValue }
    ) => {
      try {
        return UpdateSpeaker({ speaker, baseLanguage, languages });
      } catch (error) {
        return rejectWithValue(serializeError(error));
      }
    },
    {
      fulfilled: (state, action) => {
        const updatedSpeaker = action.payload;
        if (updatedSpeaker && state.speakers) {
          state.speakers = state.speakers.map((speaker) => {
            if (speaker.speaker === updatedSpeaker.speaker) {
              return updatedSpeaker;
            }
            return speaker;
          });
        }
      },
      rejected: (_state, action) => {
        console.error(action.error);
        toastTrigger(
          `
            <div class="P2" style="color: var(--global-ico-text-1);">
              <div>There was an error updating your cast.</div>
              <div>Please refresh and try again.</div>
            </div>
          `,
          {
            type: 'error',
          }
        );
      },
    }
  ),
  addSpeakerToSpeakersSlice: create.reducer((state, action: PayloadAction<Speaker>) => {
    const speakers = state.speakers || [];
    const existingIndex = speakers.findIndex((s) => s.speaker === action.payload.speaker);

    if (existingIndex !== -1) {
      speakers[existingIndex] = action.payload;
    } else {
      speakers.push(action.payload);
    }

    state.speakers = speakers;
  }),
  updateSpeakerSlice: create.reducer((state, action: PayloadAction<Speaker>) => {
    if (!state.speakers) {
      state.speakers = [];
    }

    state.speakers = state.speakers.map((speaker) => {
      if (speaker.speaker === action.payload.speaker) {
        return action.payload;
      }
      return speaker;
    });
  }),
  setNewSpeakerIntoSlice: create.reducer((state, action: PayloadAction<Speaker>) => {
    if (state.loadingSpeakers) return state;

    const speakers = state.speakers || [];
    const existingIndex = speakers.findIndex((s) => s.speaker === action.payload.speaker);

    if (existingIndex !== -1) {
      speakers[existingIndex] = action.payload;
    } else {
      speakers.push(action.payload);
    }

    state.speakers = speakers;
  }),
});
